if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // 初期状態で矢印を設定
        this.handleArrowsDisplay();
        
        // 画面サイズ変更を監視
        this.onArrowsMediaChange = this.handleArrowsDisplay.bind(this);
      }

      connectedCallback() {
        if (this.onArrowsMediaChange) {
          this.mql.addEventListener('change', this.onArrowsMediaChange);
          this.handleArrowsDisplay();
        }
      }

      disconnectedCallback() {
        if (this.onArrowsMediaChange) this.mql.removeEventListener('change', this.onArrowsMediaChange);
      }

      // 画面サイズに応じて矢印の表示/非表示を制御
      handleArrowsDisplay() {
        if (this.mql.matches) {
          // PC版：矢印を表示
          this.injectArrows();
        } else {
          // モバイル版：矢印を削除
          this.removeArrows();
        }
      }

      // 矢印を削除する関数
      removeArrows() {
        const arrows = this.elements.viewer.querySelectorAll('.gallery-arrow');
        arrows.forEach(arrow => arrow.remove());
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // サムネイルが見えているかチェックして、見えていない場合はスクロール
        if (this.elements.thumbnails.isSlideVisible && !this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
          if (this.elements.thumbnails.slider && this.elements.thumbnails.slider.scrollTo) {
            this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
          }
        }
        // isSlideVisibleメソッドがない場合のフォールバック
        else if (!this.elements.thumbnails.isSlideVisible) {
          this.fallbackScrollThumbnail(thumbnail);
        }
      }

      // isSlideVisibleがない場合のフォールバック
      fallbackScrollThumbnail(thumbnail) {
        if (!thumbnail) return;
        
        const thumbnailContainer = this.elements.thumbnails;
        const sliderContainer = thumbnailContainer.slider || 
                               thumbnailContainer.querySelector('.slider') || 
                               thumbnailContainer.querySelector('[class*="slider"]') ||
                               thumbnailContainer;

        if (sliderContainer && sliderContainer.scrollTo) {
          const containerRect = sliderContainer.getBoundingClientRect();
          const thumbnailRect = thumbnail.getBoundingClientRect();
          
          // サムネイルがコンテナの表示範囲外にある場合のみスクロール
          const isVisible = thumbnailRect.left >= containerRect.left && 
                           thumbnailRect.right <= containerRect.right;
          
          if (!isVisible) {
            sliderContainer.scrollTo({ 
              left: thumbnail.offsetLeft - (containerRect.width / 2) + (thumbnailRect.width / 2), 
              behavior: 'smooth' 
            });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      // PC版のみの矢印機能 - オプション2: 枠なしシンプル
      injectArrows() {
        // 既に矢印が存在する場合は何もしない
        if (this.elements.viewer.querySelector('.gallery-arrow')) return;

        const prevBtn = document.createElement('button');
        const nextBtn = document.createElement('button');

        prevBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <polyline points="15,18 9,12 15,6"></polyline>
        </svg>`;
        nextBtn.innerHTML = `<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <polyline points="9,6 15,12 9,18"></polyline>
        </svg>`;

        prevBtn.setAttribute('aria-label', 'Previous image');
        nextBtn.setAttribute('aria-label', 'Next image');
        prevBtn.className = 'gallery-arrow prev';
        nextBtn.className = 'gallery-arrow next';

        [prevBtn, nextBtn].forEach((btn) => { btn.type = 'button'; });

        prevBtn.style.left = '8px';
        nextBtn.style.right = '8px';

        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.navigateByThumbnail(-1);
        });

        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.navigateByThumbnail(1);
        });

        this.elements.viewer.style.position = 'relative';
        this.elements.viewer.appendChild(prevBtn);
        this.elements.viewer.appendChild(nextBtn);
      }

      // PC版矢印用のナビゲーション
      navigateByThumbnail(direction) {
        const thumbnailButtons = Array.from(this.elements.thumbnails.querySelectorAll('[data-target] button'));
        const currentActiveButton = this.elements.thumbnails.querySelector('button[aria-current="true"]');
        
        if (!currentActiveButton || thumbnailButtons.length <= 1) return;

        const currentIndex = thumbnailButtons.indexOf(currentActiveButton);
        if (currentIndex === -1) return;

        let newIndex = currentIndex + direction;
        if (newIndex < 0) newIndex = thumbnailButtons.length - 1;
        if (newIndex >= thumbnailButtons.length) newIndex = 0;

        const newButton = thumbnailButtons[newIndex];
        if (newButton) {
          newButton.click();
        }
      }
    }
  );
}